import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { KeycloakService } from './keycloak.service';

export type FarmerAction = 'createFarm' | 'createParcel' | 'editParcel' | 'createOrder' | 'editOrder' | 'planDelivery';

const ALL_ALLOWED: Record<FarmerAction, boolean> = {
  createFarm: true,
  createParcel: true,
  editParcel: true,
  createOrder: true,
  editOrder: true,
  planDelivery: true,
};

const FIELD_OF: Record<FarmerAction, string> = {
  createFarm: 'farmerCanCreateFarm',
  createParcel: 'farmerCanCreateParcel',
  editParcel: 'farmerCanEditParcel',
  createOrder: 'farmerCanCreateOrder',
  editOrder: 'farmerCanEditOrder',
  planDelivery: 'farmerCanPlanDelivery',
};

/** What the current farmer-only user may do, from block 6 of their organisation (C2027-196). */
@Injectable({ providedIn: 'root' })
export class FarmerAuthorizationsService {
  private authorizations$: Observable<Record<FarmerAction, boolean>>;

  constructor(private http: HttpClient) {}

  /** Emits once and caches; resolves to "everything allowed" when the user is not farmer-only. */
  load(): Observable<Record<FarmerAction, boolean>> {
    if (!KeycloakService.isFarmerOnly()) {
      return of(ALL_ALLOWED);
    }
    if (!this.authorizations$) {
      this.authorizations$ = this.http.get<any>('/me').pipe(
        map((response) => this.toAuthorizations(response?.data?.organisation)),
        catchError((error) => {
          console.error('Could not read the farmer authorizations from /me', error);
          return of(ALL_ALLOWED);
        }),
        shareReplay(1),
      );
    }
    return this.authorizations$;
  }

  can(action: FarmerAction): Observable<boolean> {
    return this.load().pipe(map((authorizations) => authorizations[action]));
  }

  private toAuthorizations(organisation: any): Record<FarmerAction, boolean> {
    const authorizations = {} as Record<FarmerAction, boolean>;
    for (const action of Object.keys(FIELD_OF) as FarmerAction[]) {
      const value = organisation?.[FIELD_OF[action]];
      authorizations[action] = value !== false;
    }
    return authorizations;
  }
}
